import { useState, useEffect } from 'react';
import './Giveaway.css';
import './TwitchTimers.css';

export default function TwitchTimers() {
  const [timers, setTimers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [showAdd, setShowAdd] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [newInterval, setNewInterval] = useState(5);

  const [twitchUsername, setTwitchUsername] = useState(localStorage.getItem('twitch_username') || '');

  const loadTimers = async () => {
    try {
      const token = localStorage.getItem('discord_token');
      const res = await fetch(`${process.env.REACT_APP_API_BASE || '/api'}/bot-timers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTimers(data);
      }
    } catch (e) {
      console.error(e);
      setError('Fehler beim Laden der Timer.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTimers();
  }, []);

  const handleAddTimer = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || newInterval < 1) return;
    if (!twitchUsername) {
      setError('Bitte verknüpfe zuerst deinen Twitch-Account in der Account Info Seite.');
      return;
    }

    try {
      const token = localStorage.getItem('discord_token');
      const res = await fetch(`${process.env.REACT_APP_API_BASE || '/api'}/bot-timers`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          channel: twitchUsername,
          message: newMessage,
          interval_minutes: newInterval
        })
      });

      if (res.ok) {
        setNewMessage('');
        setNewInterval(5);
        setShowAdd(false);
        loadTimers();
      } else {
        const data = await res.json();
        setError(data.error || 'Fehler beim Erstellen.');
      }
    } catch (e) {
      console.error(e);
      setError('Fehler beim Erstellen.');
    }
  };

  const handleToggleTimer = async (id, currentActive) => {
    try {
      const token = localStorage.getItem('discord_token');
      await fetch(`${process.env.REACT_APP_API_BASE || '/api'}/bot-timers/${id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !currentActive })
      });
      loadTimers();
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteTimer = async (id) => {
    if (!window.confirm('Timer wirklich löschen?')) return;
    try {
      const token = localStorage.getItem('discord_token');
      await fetch(`${process.env.REACT_APP_API_BASE || '/api'}/bot-timers/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      loadTimers();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="page twitch-timers-page">
      <h1>⏱️ Auto-Nachrichten (Timers)</h1>
      <p className="muted" style={{ marginBottom: '20px' }}>
        Der Bot sendet diese Nachrichten automatisch in festgelegten Zeitabständen in deinen Twitch Chat.
      </p>

      {!twitchUsername && (
        <div className="giveaway-twitch-not-connected" style={{ marginBottom: '20px' }}>
          <p>🔑 Bitte verbinde zuerst deinen Twitch-Account unter "Account Info", bevor du Timer anlegst.</p>
        </div>
      )}

      {error && <div className="giveaway-twitch-error" style={{ marginBottom: '20px' }}>⚠️ {error}</div>}

      <div className="timers-header">
        <button 
          className={showAdd ? "giveaway-btn-end" : "giveaway-btn-create"}
          onClick={() => setShowAdd(!showAdd)}
          disabled={!twitchUsername}
        >
          {showAdd ? 'Abbrechen' : '+ Neuer Timer'}
        </button>
      </div>

      {showAdd && (
        <div className="giveaway-twitch-card new-timer-card">
          <h3>Neuen Timer anlegen</h3>
          <form onSubmit={handleAddTimer}>
            <div className="form-group">
              <label>Nachrichtentext</label>
              <textarea 
                className="timers-textarea"
                placeholder="z.B. Schaut auch gerne auf meinem Discord vorbei! !discord"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                maxLength={450}
                required
              />
            </div>
            <div className="form-group">
              <label>Intervall (in Minuten)</label>
              <input 
                type="number"
                className="timers-input"
                min="1"
                max="1440"
                value={newInterval}
                onChange={(e) => setNewInterval(parseInt(e.target.value) || 5)}
                required
              />
              <small className="muted">Wie oft soll die Nachricht gesendet werden?</small>
            </div>
            <button type="submit" className="giveaway-btn-create" disabled={!newMessage.trim() || newInterval < 1}>
              Speichern
            </button>
          </form>
        </div>
      )}

      {loading ? (
        <p className="muted">Lade Timer...</p>
      ) : timers.length === 0 ? (
        <div className="giveaway-twitch-card" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <p className="muted">Du hast noch keine Timer angelegt.</p>
        </div>
      ) : (
        <div className="timers-list">
          {timers.map(timer => (
            <div key={timer.id} className={`giveaway-twitch-card timer-card ${timer.is_active ? 'active-timer' : 'inactive-timer'}`}>
              <div className="timer-content">
                <p className="timer-message">"{timer.message}"</p>
                <div className="timer-meta">
                  <span className="timer-badge">⏱️ {timer.interval_minutes} Min</span>
                  <span className="timer-badge">📍 #{timer.channel}</span>
                  {timer.last_sent_at && (
                    <span className="timer-badge">Zuletzt: {new Date(timer.last_sent_at).toLocaleTimeString('de-DE')}</span>
                  )}
                </div>
              </div>
              <div className="timer-actions">
                <button 
                  className={timer.is_active ? 'giveaway-btn-end' : 'giveaway-btn-create'}
                  onClick={() => handleToggleTimer(timer.id, timer.is_active)}
                >
                  {timer.is_active ? 'Deaktivieren' : 'Aktivieren'}
                </button>
                <button 
                  className="giveaway-btn-end"
                  onClick={() => handleDeleteTimer(timer.id)}
                  style={{ marginLeft: '10px' }}
                >
                  Löschen
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
